import isClient from 'commons/isClient';

const createTimeoutError = timeoutMs =>
  new Error(`observeDomCondition: condition not met within ${timeoutMs}ms`);

const getNodesToObserve = ({ getTargets, getTarget }) => {
  const targets = getTargets?.() ?? [getTarget()];
  const nodes = targets.filter(
    target => target?.nodeType === Node.ELEMENT_NODE
  );

  return nodes.length ? nodes : [document.body];
};

const observeDomCondition = ({
  callback = () => {},
  condition = () => true,
  errorCallback = () => {},
  getTarget = () => document.body,
  getTargets,
  getValue,
  observerOptions = { childList: true, subtree: true },
  timeout = 20000,
}) => {
  if (!isClient()) {
    return () => {};
  }

  const state = {
    settled: false,
    observer: null,
    timeoutId: null,
  };

  const cleanup = () => {
    state.observer?.disconnect();
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }
  };

  const settle = fn => {
    if (state.settled) {
      return;
    }

    state.settled = true;
    cleanup();
    fn();
  };

  const check = () => {
    if (getValue) {
      const value = getValue();

      if (value && (!(value instanceof Node) || value.isConnected)) {
        settle(() => callback(value));
        return true;
      }

      return false;
    }

    if (condition()) {
      settle(callback);
      return true;
    }

    return false;
  };

  if (check()) {
    return () => {};
  }

  if (typeof MutationObserver !== 'undefined') {
    state.observer = new MutationObserver(check);
    getNodesToObserve({ getTargets, getTarget }).forEach(node => {
      state.observer.observe(node, observerOptions);
    });
  }

  state.timeoutId = setTimeout(() => {
    settle(() => errorCallback(createTimeoutError(timeout)));
  }, timeout);

  return cleanup;
};

export const observeDomConditionAsync = options =>
  new Promise((resolve, reject) => {
    observeDomCondition({
      ...options,
      callback: resolve,
      errorCallback: reject,
    });
  });

export default observeDomCondition;
